import React from 'react'
import { observer } from 'mobx-react'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import Pill from 'components/Pill'
import { CheckboxCell, DateCell, TableCell, TextCell } from 'components/Table'
import { SearchableUser } from 'iam/models/SearchableUser'
import { UserManagementViewModel } from 'user-management/views/UserManagement/UserManagementViewModel'
import Workspace from '../../../iam/models/Workspace'
import { ImageCellContent } from 'components/Table/TableCell'
import { formatUserDataType } from '../../formatters/user-data-type-formatter'

interface UserManagementTableRowProps {
  vm: UserManagementViewModel
  user: SearchableUser
}

interface UserManagementNameCellWithIndicatorProps {
  title?: string | null
  image?: string | null
  imageFallback?: string | null
  size?: number
  minWidth?: number
  noPadding?: boolean
  dataTest?: string
  online?: boolean
}

const UserManagementNameCellWithIndicator = observer(function ImageCell({
  title,
  image,
  imageFallback,
  size,
  minWidth,
  noPadding,
  dataTest,
  online,
}: UserManagementNameCellWithIndicatorProps) {
  return (
    <TableCell dataTest={dataTest}>
      <ImageCellContent
        title={title}
        image={image}
        size={size}
        imageFallback={imageFallback}
        minWidth={minWidth}
        noPadding={noPadding}
        online={online}
      />
    </TableCell>
  )
})

export default observer(({ vm, user }: UserManagementTableRowProps) => (
  <>
    <CheckboxCell
      dataTest={`user-management-table-row-toggle-selection-${user.publicId}`}
      checked={vm.isSelected(user.publicId)}
      onChange={(checked) => vm.toggleUser(checked, user.publicId)}
    />

    <UserManagementNameCellWithIndicator
      key={user.publicId}
      title={user.displayName}
      dataTest={`user-management-table-row-user-${user.publicId}`}
      online={user.online}
    />

    <TextCell>{user.email}</TextCell>

    <TableCell>
      {user.userDataType && (
        <Pill color={user.isWorkspaceCustomer ? 'green' : undefined}>
          {formatUserDataType(user.userDataType)}
        </Pill>
      )}
    </TableCell>

    <DateCell date={user.lastSeen} />

    <DateCell date={user.dateCreated} />

    <TableCell>
      <Dropdown
        autoDismiss
        position="bottom-end"
        dataTest={`user-management-table-row-actions-${user.publicId}`}
        button={
          <IconButton>
            <Icon name="more" color="var(--grey600)" />
          </IconButton>
        }
      >
        <DropdownTitle>Actions</DropdownTitle>
        <DropdownItem
          onClick={() => vm.workspaceInvitationVM.showWithUser(user)}
          icon={<Icon name="add" color="var(--grey600)" />}
          dataTest="user-management-table-row-actions-invite-to-workspace"
          muteClickEvent
        >
          Invite to workspace
        </DropdownItem>
        {vm.workspaceSelector.selectedWorkspace === Workspace.none &&
          vm.isPlatformAdmin && (
            <DropdownItem
              onClick={() =>
                vm.manageUserCapacityVM.showWithUser(
                  user,
                  vm.workspaceSelector.selectedWorkspace,
                )
              }
              icon={<Icon name="edit-outlined" color="var(--grey600)" />}
              dataTest="user-management-table-row-actions-manage-global-capacity"
              muteClickEvent
            >
              Manage global capacity
            </DropdownItem>
          )}
        {/* Only display these actions when filtering by a workspace. */}
        {vm.workspaceSelector.selectedWorkspace.id && (
          <>
            {vm.userSearch.userInactiveFilter.value ? (
              <DropdownItem
                onClick={() =>
                  vm.restoreToWorkspaceVM.showWithUser(
                    user,
                    vm.workspaceSelector.selectedWorkspace,
                  )
                }
                icon={<Icon name="add-people" color="var(--grey600)" />}
                dataTest="user-management-table-row-actions-restore-to-workspace"
                muteClickEvent
              >
                Restore to workspace
              </DropdownItem>
            ) : (
              <>
                <DropdownItem
                  onClick={() =>
                    vm.manageUserCapacityVM.showWithUser(
                      user,
                      vm.workspaceSelector.selectedWorkspace,
                    )
                  }
                  icon={<Icon name="edit-outlined" color="var(--grey600)" />}
                  dataTest="user-management-table-row-actions-manage-capacity"
                  muteClickEvent
                >
                  Manage capacity
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    vm.editUserRoleVM.showWithUser(
                      user,
                      vm.workspaceSelector.selectedWorkspace,
                    )
                  }
                  icon={<Icon name="edit-roles" color="var(--grey600)" />}
                  dataTest="user-management-table-row-actions-edit-roles"
                  muteClickEvent
                >
                  Edit role
                </DropdownItem>
                <DropdownItem
                  onClick={() => vm.manageSkillsVM.showWithUser(user)}
                  icon={<Icon name="edit-skills" color="var(--grey600)" />}
                  dataTest="user-management-table-row-actions-manage-skills"
                  muteClickEvent
                >
                  Manage skills
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    vm.removeFromWorkspaceVM.showWithUser(
                      user,
                      vm.workspaceSelector.selectedWorkspace,
                    )
                  }
                  icon={<Icon name="close" color="var(--grey600)" />}
                  dataTest="user-management-table-row-actions-remove-from-workspace"
                  muteClickEvent
                >
                  Remove from workspace
                </DropdownItem>
              </>
            )}
          </>
        )}
      </Dropdown>
    </TableCell>
  </>
))
